import React, { useEffect, useState } from 'react';
import { Clock, Users, CheckCircle, ExternalLink } from 'lucide-react';

interface PendingApprovalProps {
    groupName: string;
    groupImage: string | null;
    platform?: 'whatsapp' | 'signal' | 'telegram';
    telegramSubscriberCount?: number | null;
    redirectUrl?: string | null;
}

export function PendingApproval({ groupName, groupImage, platform = 'whatsapp', telegramSubscriberCount, redirectUrl }: PendingApprovalProps) {
    const [countdown, setCountdown] = useState(3);

    useEffect(() => {
        if (!redirectUrl) return;
        if (countdown <= 0) {
            window.location.href = redirectUrl;
            return;
        }
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [redirectUrl, countdown]);
    const isSignal = platform === 'signal';
    const isTelegram = platform === 'telegram';
    const bgColor = isSignal ? 'bg-[#3A76F0]' : isTelegram ? 'bg-[#0088CC]' : 'bg-[#00A884]';
    const accentColor = isSignal ? 'bg-[#3A76F0]' : isTelegram ? 'bg-[#0088CC]' : 'bg-[#00A884]';
    const platformName = isSignal ? 'Signal' : isTelegram ? 'Telegram' : 'WhatsApp';
    const telegramSubscriberLabel = typeof telegramSubscriberCount === 'number'
        ? `${telegramSubscriberCount.toLocaleString()} ${telegramSubscriberCount === 1 ? 'subscriber' : 'subscribers'}`
        : 'Private group';
    const telegramPatternUrl = '/telegram-pattern-real.svg';
    const telegramDesktopGlowStyle = {
        backgroundImage: [
            'radial-gradient(circle at 14% 18%, rgba(88, 116, 85, 0.44) 0, rgba(88, 116, 85, 0.44) 14%, rgba(88, 116, 85, 0) 36%)',
            'radial-gradient(circle at 76% 14%, rgba(95, 84, 118, 0.24) 0, rgba(95, 84, 118, 0.24) 12%, rgba(95, 84, 118, 0) 30%)',
            'radial-gradient(circle at 18% 84%, rgba(115, 129, 79, 0.28) 0, rgba(115, 129, 79, 0.28) 13%, rgba(115, 129, 79, 0) 34%)',
            'radial-gradient(circle at 82% 78%, rgba(76, 109, 89, 0.26) 0, rgba(76, 109, 89, 0.26) 12%, rgba(76, 109, 89, 0) 32%)',
        ].join(', '),
    };
    const telegramDesktopPatternBaseStyle = {
        backgroundImage: `url('${telegramPatternUrl}')`,
        backgroundSize: '420px',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'repeat',
        mixBlendMode: 'overlay' as const,
        opacity: 0.3,
    };
    const telegramDesktopPatternTintStyle = {
        backgroundImage: `url('${telegramPatternUrl}')`,
        backgroundSize: '420px',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'repeat',
        opacity: 0.16,
        filter: 'invert(74%) sepia(10%) saturate(350%) hue-rotate(46deg) brightness(68%) contrast(86%)',
    };
    const telegramMobileCanvasStyle = {
        backgroundImage: [
            'radial-gradient(circle at 18% 14%, rgba(107, 165, 135, 0.28) 0, rgba(107, 165, 135, 0.28) 16%, rgba(107, 165, 135, 0) 40%)',
            'radial-gradient(circle at 78% 82%, rgba(213, 216, 141, 0.32) 0, rgba(213, 216, 141, 0.32) 14%, rgba(213, 216, 141, 0) 38%)',
            'linear-gradient(180deg, #eef2e6 0%, #d2dfc7 100%)',
        ].join(', '),
    };
    const telegramMobilePatternStyle = {
        backgroundImage: `url('${telegramPatternUrl}')`,
        backgroundSize: '210px',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'repeat',
        mixBlendMode: 'overlay' as const,
        opacity: 0.24,
    };

    if (isTelegram) {
        return (
            <div className="relative min-h-screen overflow-hidden">
                <div className="absolute inset-0 hidden md:block bg-[linear-gradient(135deg,#151018_0%,#101418_26%,#141913_58%,#17120f_100%)]" />
                <div className="absolute inset-[-14%] hidden md:block opacity-90 blur-[96px]" style={telegramDesktopGlowStyle} />
                <div className="absolute inset-0 hidden md:block" style={telegramDesktopPatternBaseStyle} />
                <div className="absolute inset-0 hidden md:block" style={telegramDesktopPatternTintStyle} />
                <div className="absolute inset-0 md:hidden" style={telegramMobileCanvasStyle} />
                <div className="absolute inset-0 md:hidden" style={telegramMobilePatternStyle} />

                <div className="relative z-10 flex min-h-screen flex-col">
                    <div className="flex items-center justify-between border-b border-black/5 bg-white px-4 py-4 md:border-b-0 md:bg-transparent md:px-8 md:py-5">
                        <div className="flex items-center gap-3 md:text-white">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#3390ec] text-white shadow-sm">
                                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                                    <path d="M21.75 4.35a1.2 1.2 0 0 0-1.27-.18L3.47 11.56a1.1 1.1 0 0 0 .1 2.05l4.57 1.55 1.62 5.1a1.1 1.1 0 0 0 1.98.23l2.56-3.53 4.5 3.28a1.2 1.2 0 0 0 1.9-.7l2.2-13.77a1.2 1.2 0 0 0-.45-1.12ZM9.2 14.53l8.7-6.45-7.07 7.82-.66 2.74-.97-4.11Z" />
                                </svg>
                            </div>
                            <span className="text-[1.05rem] font-semibold text-[#1f2c38] md:text-white">Telegram</span>
                        </div>
                        <a
                            href="https://telegram.org/dl"
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-full bg-[#3390ec] px-5 py-2 text-sm font-semibold uppercase tracking-[0.02em] text-white shadow-sm transition hover:bg-[#2a82d7]"
                        >
                            Download
                        </a>
                    </div>

                    <div className="flex flex-1 items-start justify-center px-4 pb-10 pt-10 md:items-center md:px-8 md:pt-0">
                        <div className="w-full max-w-[392px] md:max-w-[306px]">
                            <div className="rounded-[28px] bg-white px-6 py-8 shadow-[0_16px_48px_rgba(28,39,56,0.18)] md:rounded-[22px] md:bg-[#1f1f1f] md:px-8 md:py-9 md:shadow-[0_18px_50px_rgba(0,0,0,0.45)]">
                                <div className="mx-auto mb-5 flex h-28 w-28 items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-[#6cc8ff] to-[#3390ec] shadow-lg md:mb-4 md:h-24 md:w-24">
                                    {groupImage ? (
                                        <img src={groupImage} alt={groupName} className="h-full w-full object-cover" />
                                    ) : (
                                        <span className="text-5xl font-semibold text-white md:text-[2.6rem]">{groupName.charAt(0).toUpperCase()}</span>
                                    )}
                                </div>
                                <h1 className="text-center text-[2.1rem] font-bold leading-none text-black md:text-[2rem] md:text-white">{groupName}</h1>
                                <p className="mt-3 text-center text-[1rem] text-[#7c7c7c] md:mt-2 md:text-[0.95rem] md:text-[#9aa3ab]">{telegramSubscriberLabel}</p>

                                <div className="mt-8 rounded-[24px] bg-[#f5fbff] px-5 py-5 text-center md:mt-6 md:bg-[#243343]">
                                    <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[#dff2ff] md:bg-[#1d4771]">
                                        <CheckCircle className="h-8 w-8 text-[#3390ec] md:text-[#5eb8ff]" />
                                    </div>
                                    <h2 className="text-lg font-semibold text-[#1f2c38] md:text-white">{redirectUrl ? 'Joining Group...' : 'Request Sent!'}</h2>
                                    <p className="mt-2 text-sm leading-6 text-[#60717c] md:text-[#afbcc6]">
                                        {redirectUrl ? (
                                            <>Redirecting you to <span className="font-semibold text-[#24323d] md:text-white">{groupName}</span> in <span className="font-bold text-[#24323d] md:text-white">{countdown}</span> seconds...</>
                                        ) : (
                                            <>Your request to join <span className="font-semibold text-[#24323d] md:text-white">{groupName}</span> has been sent successfully.</>
                                        )}
                                    </p>

                                    {redirectUrl ? (
                                        <a
                                            href={redirectUrl}
                                            className="mx-auto mt-5 flex min-h-[50px] min-w-[176px] items-center justify-center rounded-full bg-[#3390ec] px-8 text-base font-semibold uppercase tracking-[0.02em] text-white transition hover:bg-[#2a82d7] md:min-h-[46px] md:min-w-[132px] md:px-6 md:text-sm"
                                        >
                                            <ExternalLink className="mr-2 h-4 w-4" />
                                            Join Now
                                        </a>
                                    ) : (
                                        <div className="mt-5 flex items-center justify-center gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700 md:bg-[#3d3111] md:text-amber-200">
                                            <Clock className="h-4 w-4" />
                                            Waiting for admin approval
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className={`min-h-screen ${bgColor} flex items-center justify-center p-4`}>
            <div className="w-full max-w-md">
                {/* Main Card */}
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="pt-8 pb-6 px-6 text-center">
                        {/* Group Image */}
                        <div className={`w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden ${accentColor} flex items-center justify-center shadow-lg`}>
                            {groupImage ? (
                                <img 
                                    src={groupImage} 
                                    alt={groupName} 
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <Users className="w-12 h-12 text-white" />
                            )}
                        </div>
                        
                        {/* Group Name */}
                        <h1 className="text-xl font-semibold text-gray-900 mb-1">
                            {groupName}
                        </h1>
                        <p className="text-sm text-gray-500">{platformName} Group</p>
                    </div>

                    {/* Success Message */}
                    <div className="px-6 pb-8">
                        <div className="bg-green-50 rounded-2xl p-6 text-center">
                            {/* Success Icon */}
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle className="w-10 h-10 text-green-500" />
                            </div>

                            <h2 className="text-lg font-semibold text-gray-900 mb-2">
                                {redirectUrl ? 'Joining Group...' : 'Request Sent!'}
                            </h2>
                            
                            <p className="text-gray-600 mb-4">
                                {redirectUrl ? (
                                    <>Redirecting you to <span className="font-medium text-gray-900">{groupName}</span> in <span className="font-bold text-lg">{countdown}</span> seconds...</>
                                ) : (
                                    <>Your request to join <span className="font-medium text-gray-900">{groupName}</span> has been sent successfully.</>
                                )}
                            </p>

                            {redirectUrl ? (
                                <a
                                    href={redirectUrl}
                                    className={`flex items-center justify-center gap-2 text-white ${accentColor} rounded-xl py-3 px-4 hover:opacity-90 transition-opacity`}
                                >
                                    <ExternalLink className="w-5 h-5" />
                                    <span className="text-sm font-medium">
                                        Join Now
                                    </span>
                                </a>
                            ) : (
                                <>
                                    {/* Pending Status */}
                                    <div className="flex items-center justify-center gap-2 text-amber-600 bg-amber-50 rounded-xl py-3 px-4">
                                        <Clock className="w-5 h-5" />
                                        <span className="text-sm font-medium">
                                            Waiting for admin approval
                                        </span>
                                    </div>

                                    <p className="text-sm text-gray-500 mt-4">
                                        An admin must approve your request before you can join the group. You'll receive a notification once approved.
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Platform Branding */}
                <div className="mt-6 text-center">
                    <div className="flex items-center justify-center gap-2 text-white/80">
                        {isSignal ? (
                            // Signal icon
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93s3.05-7.44 7-7.93v15.86zm2-15.86c3.94.49 7 3.85 7 7.93s-3.05 7.44-7 7.93V4.07z"/>
                            </svg>
                        ) : (
                            // WhatsApp icon
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                        )}
                        <span className="text-sm font-medium">{platformName}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
